<template>
  <a-modal
      v-model:visible="visible"
      :title="isEdit ? '编辑字典数据' : '新增字典数据'"
      @cancel="handleCancel"
      :on-before-ok="handleSubmit"
      width="600px"
      draggable
  >
    <div class="edit-container">
      <a-form
          ref="formRef"
          :model="formData"
          :rules="formRules"
          layout="vertical"
      >
        <a-form-item label="数据标签" field="dictLabel">
          <a-input 
              v-model="formData.dictLabel" 
              placeholder="请输入数据标签"
              allow-clear
          />
        </a-form-item>
        
        <a-form-item label="数据值" field="dictValue">
          <a-input 
              v-model="formData.dictValue" 
              placeholder="请输入数据值"
              allow-clear
          />
        </a-form-item>
        
        <a-form-item label="排序" field="sort">
          <a-input-number 
              v-model="formData.sort" 
              placeholder="请输入排序值"
              :min="0"
              :max="999"
              style="width: 100%"
          />
        </a-form-item>
        
        <a-form-item label="是否默认" field="isDefault">
          <a-radio-group v-model="formData.isDefault">
            <a-radio :value="true">是</a-radio>
            <a-radio :value="false">否</a-radio>
          </a-radio-group>
        </a-form-item>
        
        <a-form-item label="状态" field="status">
          <a-radio-group v-model="formData.status">
            <a-radio :value="true">启用</a-radio>
            <a-radio :value="false">停用</a-radio>
          </a-radio-group>
        </a-form-item>
        
        <a-form-item label="备注" field="remark">
          <a-textarea 
              v-model="formData.remark" 
              placeholder="请输入备注"
              :auto-size="{ minRows: 3, maxRows: 6 }"
              allow-clear
          />
        </a-form-item>
      </a-form>
    </div>
  </a-modal>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue'
import { Message } from '@arco-design/web-vue'
import { getSysDictDataInfo, createSysDictData, updateSysDictData } from '@/apis/system-data'

// 定义 props
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  dictDataId: {
    type: [String, Number],
    default: null
  },
  dictTypeId: {
    type: [String, Number],
    default: null
  }
})

// 定义 emits
const emit = defineEmits(['update:modelValue', 'success'])

// 响应式数据
const visible = ref(false)
const loading = ref(false)
const formRef = ref(null)
const isEdit = ref(false)

// 表单数据
const formData = ref({
  dictLabel: '',
  dictValue: '',
  sort: 0,
  isDefault: false,
  status: true,
  remark: ''
})

// 表单验证规则
const formRules = ref({
  dictLabel: [
    { required: true, message: '请输入数据标签' },
    { minLength: 1, maxLength: 50, message: '数据标签长度为1-50个字符' }
  ],
  dictValue: [
    { required: true, message: '请输入数据值' },
    { minLength: 1, maxLength: 50, message: '数据值长度为1-50个字符' }
  ],
  sort: [
    { required: true, message: '请输入排序值' },
    { type: 'number', min: 0, max: 999, message: '排序值范围为0-999' }
  ],
  isDefault: [
    { required: true, message: '请选择是否默认' }
  ],
  status: [
    { required: true, message: '请选择状态' }
  ],
  remark: [
    { maxLength: 500, message: '备注最多500个字符' }
  ]
})

// 监听 modelValue 变化
watch(() => props.modelValue, (newVal) => {
  visible.value = newVal
  if (newVal) {
    resetForm()
    if (props.dictDataId) {
      isEdit.value = true
      fetchDictDataInfo()
    } else {
      isEdit.value = false
    }
  }
})

// 监听 visible 变化
watch(visible, (newVal) => {
  emit('update:modelValue', newVal)
})

// 重置表单
const resetForm = () => {
  formData.value = {
    id: '',
    dictLabel: '',
    dictValue: '',
    sort: 0,
    isDefault: false,
    status: true,
    remark: ''
  }
  nextTick(() => {
    if (formRef.value) {
      formRef.value?.clearValidate()
    }
  })
}

// 获取字典数据详情
const fetchDictDataInfo = async () => {
  if (!props.dictDataId) return
  
  loading.value = true
  try {
    const response = await getSysDictDataInfo(props.dictDataId)
    if (response.code === 0) {
      formData.value = response.data
    } else {
      Message.error(response.message || '获取字典数据详情失败')
    }
  } catch (error) {
    Message.error(error.message || '获取字典数据详情失败')
    console.error('获取字典数据详情失败:', error)
  } finally {
    loading.value = false
  }
}

// 提交表单
const handleSubmit = async (done) => {
  try {
    const validateResult = await formRef.value?.validate()

    // 如果验证失败，阻止提交和关闭弹框
    if (validateResult !== undefined) {
      done(false)
      return false
    }
    
    loading.value = true
    let response
    
    const submitData = {
      ...formData.value,
      typeId: props.dictTypeId
    }
    
    if (isEdit.value) {
      // 编辑模式
      response = await updateSysDictData(props.dictDataId, submitData)
    } else {
      // 新增模式
      response = await createSysDictData(submitData)
    }
    
    if (response.code === 0) {
      Message.success(isEdit.value ? '更新成功' : '创建成功')
      emit('success')
      visible.value = false
      return true
    } else {
      Message.error(response.message || '操作失败')
      done(false)
      return false
    }
  } catch (error) {
    Message.error(error.message || '操作失败')
    console.error('提交表单失败:', error)
    done(false)
    return false
  } finally {
    done(true)
    loading.value = false
  }
}

// 取消操作
const handleCancel = () => {
  visible.value = false
}
</script>

<style scoped>
.edit-container {
  padding: 0;
}
</style>